"use client";

import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { KARNAUGH_MAP_SOLVER_STRINGS } from "@/components/tools/karnaugh-map-solver";
import { t } from "@/lib/i18n/interpolate";
import { type Minimization, patternOf } from "@/lib/tool-kit/algebra/minimize";

type Strings = typeof KARNAUGH_MAP_SOLVER_STRINGS;

const label = (index: number) => `P${index + 1}`;

const ones = (value: number): number => {
  let count = 0;
  let rest = value;
  while (rest > 0) {
    count += rest % 2;
    rest = Math.floor(rest / 2);
  }
  return count;
};

/**
 * The Quine–McCluskey working for one minimisation: the merge rounds, the prime implicant
 * chart, the essential implicants, and Petrick's method when the chart needs it.
 */
export function QmView({
  min,
  term,
  strings,
}: {
  min: Minimization;
  /** How an implicant is written in the chart. */
  term: (index: number) => string;
  strings: Strings;
}) {
  const { n } = min;
  const pi = (indexes: number[]) => indexes.map(label).join(" + ");
  const chosen = pi(min.cover);
  return (
    <div className="space-y-4">
      {min.rounds.map((round, roundIndex) => (
        <div
          className="space-y-2"
          key={round.entries.map((e) => patternOf(e.implicant, n)).join("|")}
        >
          <p className="font-medium text-foreground text-sm">
            {t(roundIndex === 0 ? strings.qmStart : strings.qmRound, {
              n: roundIndex + 1,
            })}
          </p>
          <DataTable
            head={[
              strings.pattern,
              strings.mintermsColumn,
              strings.onesColumn,
              strings.status,
            ]}
            rows={round.entries.map((entry) => [
              patternOf(entry.implicant, n),
              entry.implicant.terms.join(", "),
              ones(entry.implicant.value),
              entry.combined ? strings.merged : strings.prime,
            ])}
          />
        </div>
      ))}
      {min.primes.length === 0 ? null : (
        <>
          <h4 className={WIDGET_H3}>{strings.chartHeading}</h4>
          <p className="text-muted-foreground text-sm">{strings.chartIntro}</p>
          <DataTable
            head={[
              strings.primeColumn,
              strings.pattern,
              strings.termColumn,
              ...min.required.map(String),
              strings.essentialColumn,
            ]}
            rows={min.primes.map((prime, index) => [
              label(index),
              patternOf(prime, n),
              term(index),
              ...min.required.map((m) => (prime.terms.includes(m) ? "●" : "")),
              min.essential.includes(index) ? "★" : "",
            ])}
          />
          <p className="text-foreground text-sm">
            {min.essential.length > 0
              ? t(strings.essentialLine, { list: pi(min.essential) })
              : strings.noEssential}
          </p>
        </>
      )}
      {min.petrick ? (
        <div className="space-y-2">
          <h4 className={WIDGET_H3}>{strings.petrickHeading}</h4>
          <p className="text-muted-foreground text-sm">
            {t(strings.petrickIntro, {
              minterms: min.petrick.remaining.join(", "),
            })}
          </p>
          <p className="break-words font-mono text-foreground text-sm">
            {min.petrick.sums.map((sum) => `(${pi(sum)})`).join("")}
          </p>
          <p className="text-muted-foreground text-sm">
            {strings.petrickExpanded}
          </p>
          <p className="break-words font-mono text-foreground text-sm">
            {min.petrick.products
              .map((product) => product.map(label).join(""))
              .join(" + ")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(strings.petrickChoice, { choice: chosen })}
          </p>
          {min.alternatives.length > 1 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.tieNote, {
                count: min.alternatives.length,
                list: min.alternatives.map((alt) => pi(alt)).join(" | "),
              })}
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="font-medium text-foreground text-sm">
        {t(strings.coverLine, {
          cover: chosen === "" ? "—" : chosen,
          literals: min.literals,
        })}
      </p>
    </div>
  );
}
